import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fmtMonto } from 'shared/format';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import { FiPhone, FiMail } from 'react-icons/fi';

const money = (n) => `$${fmtMonto(n)}`;

export default function VendedorPanel() {
  const { tienePermiso } = useAuth();
  const { notificar } = useNotificaciones();
  const { COL } = useDarkMode();

  const [tab, setTab] = useState('clientes');
  const [clientes, setClientes] = useState([]);
  const [resumen, setResumen] = useState({ pendiente: 0, pagada: 0, anulada: 0, cobrado_total: 0 });
  const [detalle, setDetalle] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [clienteSel, setClienteSel] = useState(null);

  const puedeCobrar = tienePermiso('vendedores.cobrar');
  const S = useMemo(() => buildStyles(COL), [COL]);

  const cargar = useCallback(async () => {
    try {
      const [rC, rM, rB, rD] = await Promise.all([
        API.get('/vendedores/mis-clientes'),
        API.get('/vendedores/mis-comisiones'),
        API.get('/vendedores/mis-cobros'),
        API.get('/vendedores/mis-creditos'),
      ]);
      if (rC.data.status === 'success') setClientes(rC.data.data);
      if (rM.data.status === 'success') {
        setResumen(rM.data.data.resumen || {});
        setDetalle(rM.data.data.detalle || []);
      }
      if (rB.data.status === 'success') setCobros(rB.data.data);
      if (rD.data.status === 'success') setCreditos(rD.data.data);
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: 'No se pudo cargar tu panel', tipo: 'error' });
    } finally {
      setCargando(false);
    }
  }, [notificar]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) return <div style={S.wrap}>Cargando...</div>;

  const tabs = [
    { id: 'clientes', label: 'Clientes' },
    ...(puedeCobrar ? [{ id: 'cobrar', label: 'Registrar cobro' }] : []),
    { id: 'cobros', label: 'Cobros' },
    { id: 'comisiones', label: 'Comisiones' },
  ];

  return (
    <div style={S.wrap}>
      <h2 style={S.title}>Mis Comisiones</h2>
      <div style={S.subtitle}>Clientes asignados, cobros y comisiones por cobro</div>

      <div style={S.stats}>
        <div style={S.stat}><div style={S.statLbl}>Comisión pendiente</div><div style={{ ...S.statVal, color: COL.amber }}>{money(resumen.pendiente)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Comisión pagada</div><div style={{ ...S.statVal, color: COL.green }}>{money(resumen.pagada)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Total cobrado</div><div style={S.statVal}>{money(resumen.cobrado_total)}</div></div>
      </div>

      <div style={S.tabs}>
        {tabs.map((t) => (
          <button key={t.id} style={{ ...S.tabBtn, ...(tab === t.id ? S.tabBtnActive : {}) }} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'cobrar' && (
        <RegistrarCobro S={S} COL={COL} creditos={creditos} onCobrado={cargar} />
      )}

      {tab === 'clientes' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Teléfono</th>
                  <th style={S.th}>RIF</th>
                  <th style={S.th}>Pendiente</th>
                  <th style={S.th}>Total gastado</th>
                  <th style={S.th}>Vencidos</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c) => (
                  <tr key={c.id}>
                    <td style={S.td}><strong>{c.nombre || c.nombre_empresa || c.id}</strong>{c.apellido ? ` ${c.apellido}` : ''}</td>
                    <td style={S.td}>{c.telefono || '-'}</td>
                    <td style={S.td}>{c.rif || '-'}</td>
                    <td style={S.td}><span style={{ color: COL.amber, fontWeight: 600 }}>{money(c.pendiente_credito)}</span></td>
                    <td style={S.td}>{money(c.total_gastado)}</td>
                    <td style={S.td}>{c.creditos_vencidos > 0 ? <span style={{ color: COL.red, fontWeight: 600 }}>{c.creditos_vencidos}</span> : 0}</td>
                    <td style={S.td}><button style={S.btnGhost} onClick={() => setClienteSel(c)}>Ver</button></td>
                  </tr>
                ))}
                {clientes.length === 0 && <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>No tienes clientes asignados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'cobros' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Monto</th>
                  <th style={S.th}>Método</th>
                  <th style={S.th}>Referencia</th>
                  <th style={S.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {cobros.map((b) => (
                  <tr key={b.id}>
                    <td style={S.td}>{b.fecha ? new Date(b.fecha).toLocaleString() : '-'}</td>
                    <td style={S.td}>{b.cliente_nombre || '-'}</td>
                    <td style={S.td}><strong>{money(b.monto)}</strong></td>
                    <td style={S.td}>{b.metodo_pago || '-'}</td>
                    <td style={S.td}>{b.referencia || '-'}</td>
                    <td style={S.td}><span style={estadoTag(b.estado, COL)}>{b.estado || '-'}</span></td>
                  </tr>
                ))}
                {cobros.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>Sin cobros</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'comisiones' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Cobro</th>
                  <th style={S.th}>%</th>
                  <th style={S.th}>Comisión</th>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {detalle.map((c) => (
                  <tr key={c.id}>
                    <td style={S.td}>{c.cliente_nombre || '-'}</td>
                    <td style={S.td}>{money(c.monto_cobro)}</td>
                    <td style={S.td}>{Number(c.porcentaje || 0)}%</td>
                    <td style={S.td}><strong>{money(c.monto_comision)}</strong></td>
                    <td style={S.td}>{c.fecha_cobro}</td>
                    <td style={S.td}><span style={{ ...S.pill, background: COL.blueBg, color: COL.pri2 }}>{c.estado}</span></td>
                  </tr>
                ))}
                {detalle.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>Sin comisiones</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {clienteSel && (
        <ModalCliente
          COL={COL}
          cliente={clienteSel}
          puedeCobrar={puedeCobrar}
          onCobrado={cargar}
          onClose={() => setClienteSel(null)}
        />
      )}
    </div>
  );
}

function ModalCliente({ COL, cliente, puedeCobrar, onCobrado, onClose }) {
  const { notificar } = useNotificaciones();
  const S = useMemo(() => buildStyles(COL), [COL]);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargarDetalle = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get(`/vendedores/cliente/${cliente.id}`);
      if (r.data.status === 'success') setData(r.data.data);
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: 'No se pudo cargar el detalle del cliente', tipo: 'error' });
    } finally {
      setCargando(false);
    }
  }, [cliente.id, notificar]);

  useEffect(() => { cargarDetalle(); }, [cargarDetalle]);

  const c = (data && data.cliente) || cliente;
  const creditos = (data && data.creditos) || [];
  const historial = (data && data.historial) || [];
  const totalPendiente = creditos.reduce((s, p) => s + Number(p.faltante || 0), 0);

  const nombreCompleto = [c.nombre, c.apellido].filter(Boolean).join(' ') || c.nombre_empresa || `Cliente #${c.id}`;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, width: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COL.text }}>{nombreCompleto}</h3>
            {c.nombre_empresa && c.nombre_empresa !== c.nombre && (
              <div style={{ fontSize: 12, color: COL.muted }}>{c.nombre_empresa}</div>
            )}
          </div>
          <button onClick={onClose} style={S.modalClose}>✕</button>
        </div>

        <div style={{ padding: '16px 22px 0' }}>
          <div style={S.detalleStats}>
            <div style={S.detStat}>
              <div style={S.statLbl}>Pendiente por cobrar</div>
              <div style={{ color: totalPendiente > 0 ? COL.amber : COL.green, fontWeight: 800, fontSize: 22 }}>{money(totalPendiente)}</div>
            </div>
            <div style={S.detStat}>
              <div style={S.statLbl}>Total gastado</div>
              <div style={{ fontWeight: 800, fontSize: 22, color: COL.text }}>{money(c.total_gastado)}</div>
            </div>
            <div style={S.detStat}>
              <div style={S.statLbl}>Créditos vencidos</div>
              <div style={{ fontWeight: 800, fontSize: 22, color: c.creditos_vencidos > 0 ? COL.red : COL.text }}>{c.creditos_vencidos || 0}</div>
            </div>
          </div>

          <div style={S.chips}>
            {c.telefono && <span style={S.chip}><FiPhone /> {c.telefono}</span>}
            {c.email && <span style={S.chip}><FiMail /> {c.email}</span>}
            {c.rif && <span style={S.chip}>RIF {c.rif}</span>}
            {c.cedula && <span style={S.chip}>CI {c.cedula}</span>}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 22px 22px', minHeight: 0 }}>
          {cargando ? (
            <div style={{ textAlign: 'center', padding: 40, color: COL.muted }}>Cargando detalle...</div>
          ) : (
            <>
              <SectionTitle COL={COL} label={`Pedidos pendientes de pago (${creditos.length})`} />
              {creditos.length === 0 ? (
                <div style={S.empty}>No tiene pedidos pendientes por pagar</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {creditos.map((p) => (
                    <CreditoCard key={p.id} p={p} COL={COL} S={S} puedeCobrar={puedeCobrar} onCobrado={cargarDetalle} />
                  ))}
                </div>
              )}

              <SectionTitle COL={COL} label="Historial de pedidos" />
              {historial.length === 0 ? (
                <div style={S.empty}>Sin pedidos</div>
              ) : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Pedido</th>
                        <th style={S.th}>Fecha</th>
                        <th style={S.th}>Estado</th>
                        <th style={S.th}>Total</th>
                        <th style={S.th}>Abonado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.map((h) => (
                        <tr key={h.id}>
                          <td style={S.td}><strong>#{h.id}</strong></td>
                          <td style={S.td}>{h.fechapedido ? new Date(h.fechapedido).toLocaleDateString('es-ES') : '-'}</td>
                          <td style={S.td}><EstadoPedido COL={COL} estado={h.estado} /></td>
                          <td style={S.td}>{money(h.total)}</td>
                          <td style={S.td}>{money(h.abonado)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <SectionTitle COL={COL} label="Datos del cliente" />
              <div style={S.detalleGrid}>
                <DetalleItem COL={COL} S={S} label="Dirección" value={c.direccion} />
                <DetalleItem COL={COL} S={S} label="Teléfono 2" value={c.telefono2} />
                <DetalleItem COL={COL} S={S} label="Tipo" value={c.tipo} />
                <DetalleItem COL={COL} S={S} label="Usuario" value={c.usuario} />
                <DetalleItem COL={COL} S={S} label="Estado" value={c.estado} />
                <DetalleItem COL={COL} S={S} label="Estado de venta" value={c.estado_venta} />
                <DetalleItem COL={COL} S={S} label="Código ERP" value={c.erp_codigo} />
                <DetalleItem COL={COL} S={S} label="Representante" value={c.representante_nombre} />
                <DetalleItem COL={COL} S={S} label="CI representante" value={c.representante_cedula} />
                <DetalleItem COL={COL} S={S} label="Tel. representante" value={c.representante_telefono} />
                <DetalleItem COL={COL} S={S} label="Observaciones" value={c.observaciones} />
                <DetalleItem COL={COL} S={S} label="Email verificado" value={c.email_verificado ? 'Sí' : 'No'} />
                <DetalleItem COL={COL} S={S} label="Registrado" value={c.fecharegistro ? new Date(c.fecharegistro).toLocaleDateString('es-ES') : null} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ COL, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 12px' }}>
      <div style={{ height: 1, flex: 1, background: COL.border }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <div style={{ height: 1, flex: 1, background: COL.border }} />
    </div>
  );
}

function DetalleItem({ COL, S, label, value }) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  return (
    <div style={S.detItem}>
      <div style={S.statLbl}>{label}</div>
      <div style={{ fontSize: 14, color: COL.text, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function EstadoPedido({ COL, estado }) {
  const map = {
    entregado: { bg: COL.greenBg, color: COL.green },
    credito_pagado: { bg: COL.greenBg, color: COL.green },
    cancelado: { bg: COL.redBg, color: COL.red },
    rechazado: { bg: COL.redBg, color: COL.red },
    pendiente: { bg: COL.amberBg, color: COL.amber },
    en_revision: { bg: COL.amberBg, color: COL.amber },
  };
  const t = map[estado] || { bg: COL.blueBg, color: COL.pri2 };
  return <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: t.bg, color: t.color }}>{estado}</span>;
}

function CreditoCard({ p, COL, S, puedeCobrar, onCobrado }) {
  const { notificar } = useNotificaciones();
  const [monto, setMonto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const pct = p.total > 0 ? Math.min(100, Math.round((p.abonado / p.total) * 100)) : 0;

  const cobrar = async () => {
    if (!monto || parseFloat(monto) <= 0) return;
    setGuardando(true);
    try {
      await API.post('/vendedores/cobrar', {
        pedido_id: Number(p.id),
        monto: parseFloat(monto),
        metodo_pago: null,
        referencia: null,
      });
      notificar({ titulo: 'Cobro registrado', mensaje: 'Queda pendiente de verificación por un administrador', tipo: 'success' });
      setMonto('');
      onCobrado();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo registrar el cobro', tipo: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const productos = Array.isArray(p.productos) ? p.productos : [];

  return (
    <div style={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 14, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 15, color: COL.text }}>Pedido #{p.id}</strong>
          <EstadoPedido COL={COL} estado={p.estado} />
          {p.vencida ? (
            <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: COL.redBg, color: COL.red }}>VENCIDO</span>
          ) : (
            p.fecha_limite_pago && (
              <span style={{ fontSize: 12, color: COL.muted }}>Vence {new Date(p.fecha_limite_pago).toLocaleDateString('es-ES')}</span>
            )
          )}
        </div>
        <span style={{ fontSize: 12, color: COL.muted }}>{new Date(p.fechapedido).toLocaleDateString('es-ES')}</span>
      </div>

      {productos.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {productos.map((prod, i) => (
            <span key={i} style={{ fontSize: 12, color: COL.muted, background: COL.surfaceAlt, padding: '3px 8px', borderRadius: 6 }}>
              {prod.nombre || prod.descripcion || prod.codigo}{prod.cantidad ? ` ×${prod.cantidad}` : ''}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: COL.muted }}>Total</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>{money(p.total)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: COL.muted }}>Abonado</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COL.green }}>{money(p.abonado)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: COL.muted }}>Faltante</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: p.faltante > 0 ? COL.amber : COL.green }}>{money(p.faltante)}</div>
        </div>
      </div>

      <div style={{ marginTop: 10, height: 6, borderRadius: 3, background: COL.skeleton, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? COL.green : COL.pri2, transition: 'width 0.3s' }} />
      </div>

      {puedeCobrar && p.faltante > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <input
            type="number" step="0.01" min="0.01" max={p.faltante}
            placeholder={`Máx ${money(p.faltante)}`}
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            style={{ ...S.input, flex: 1 }}
          />
          <button style={S.btn} disabled={guardando || !monto} onClick={cobrar}>
            {guardando ? '...' : 'Cobrar'}
          </button>
        </div>
      )}
    </div>
  );
}

function RegistrarCobro({ S, COL, creditos, onCobrado }) {
  const { notificar } = useNotificaciones();
  const [pedidoId, setPedidoId] = useState('');
  const [monto, setMonto] = useState('');
  const [metodoPago, setMetodoPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [guardando, setGuardando] = useState(false);

  const pedidoSeleccionado = creditos.find((c) => String(c.id) === String(pedidoId));

  const registrar = async () => {
    if (!pedidoId || !monto) return;
    setGuardando(true);
    try {
      await API.post('/vendedores/cobrar', {
        pedido_id: Number(pedidoId),
        monto: parseFloat(monto),
        metodo_pago: metodoPago || null,
        referencia: referencia || null,
      });
      notificar({ titulo: 'Cobro registrado', mensaje: 'Queda pendiente de verificación por un administrador', tipo: 'success' });
      setPedidoId(''); setMonto(''); setMetodoPago(''); setReferencia('');
      onCobrado();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo registrar el cobro', tipo: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={S.card}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, color: COL.text }}>Registrar cobro</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <label style={S.label}>Pedido a crédito</label>
          <select style={S.input} value={pedidoId} onChange={(e) => { setPedidoId(e.target.value); setMonto(''); }}>
            <option value="">Seleccionar pedido...</option>
            {creditos.map((c) => (
              <option key={c.id} value={c.id}>#{c.id} — {c.cliente_nombre || c.cliente_id} (faltante {money(c.faltante)})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={S.label}>Monto a cobrar</label>
          <input
            type="number" step="0.01" min="0.01"
            max={pedidoSeleccionado ? pedidoSeleccionado.faltante : undefined}
            style={S.input} value={monto}
            placeholder={pedidoSeleccionado ? `Máx ${money(pedidoSeleccionado.faltante)}` : '0.00'}
            onChange={(e) => setMonto(e.target.value)}
          />
        </div>
        <div>
          <label style={S.label}>Método de pago</label>
          <input style={S.input} value={metodoPago} placeholder="Transferencia, efectivo..." onChange={(e) => setMetodoPago(e.target.value)} />
        </div>
        <div>
          <label style={S.label}>Referencia</label>
          <input style={S.input} value={referencia} placeholder="Nº de comprobante" onChange={(e) => setReferencia(e.target.value)} />
        </div>
      </div>
      <button style={{ ...S.btn, marginTop: 16 }} disabled={guardando || !pedidoId || !monto} onClick={registrar}>
        {guardando ? 'Guardando...' : 'Registrar cobro'}
      </button>
      {creditos.length === 0 && (
        <div style={{ marginTop: 12, fontSize: 13, color: COL.muted }}>No hay pedidos a crédito pendientes de tus clientes.</div>
      )}
    </div>
  );
}

function estadoTag(estado, COL) {
  const bg = estado === 'confirmado' ? COL.greenBg : estado === 'rechazado' ? COL.redBg : COL.amberBg;
  const color = estado === 'confirmado' ? COL.green : estado === 'rechazado' ? COL.red : COL.amber;
  return { padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color };
}

function buildStyles(COL) {
  return {
    wrap: { padding: 24, fontFamily: 'system-ui, sans-serif', background: COL.bg, minHeight: '100vh', color: COL.text },
    title: { fontSize: 22, fontWeight: 700, color: COL.text, margin: 0 },
    subtitle: { fontSize: 13, color: COL.muted, marginTop: 4 },
    stats: { display: 'flex', gap: 12, margin: '20px 0', flexWrap: 'wrap' },
    stat: { flex: '1 1 180px', background: COL.surface, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18 },
    statVal: { fontSize: 24, fontWeight: 800, color: COL.pri, margin: '4px 0 0' },
    statLbl: { fontSize: 12, color: COL.muted, fontWeight: 600 },
    tabs: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
    tabBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 999, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    tabBtnActive: { background: COL.pri, color: '#fff', borderColor: COL.pri },
    card: { background: COL.surface, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 20, marginBottom: 20, overflow: 'hidden' },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 640 },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${COL.border}`, color: COL.muted, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    td: { padding: '12px', borderBottom: `1px solid ${COL.border}`, fontSize: 14, color: COL.text },
    input: { padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.border}`, fontSize: 14, width: '100%', boxSizing: 'border-box', color: COL.text, background: COL.inputBg },
    label: { fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 6, display: 'block' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: 'none', background: COL.pri, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
    btnGhost: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    pill: { padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    overlay: { position: 'fixed', inset: 0, background: COL.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 },
    modal: { background: COL.surface, borderRadius: 16, width: 560, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
    modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${COL.border}` },
    modalClose: { border: 'none', background: 'none', cursor: 'pointer', color: COL.muted, fontSize: 18, padding: 4 },
    detalleStats: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    detStat: { flex: '1 1 130px', background: COL.surfaceAlt, borderRadius: 12, padding: 12 },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
    chip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: COL.priLight, color: COL.pri2, fontSize: 12, fontWeight: 600 },
    detalleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
    detItem: { padding: '10px 12px', background: COL.surfaceAlt, borderRadius: 10 },
    empty: { fontSize: 13, color: COL.muted, textAlign: 'center', padding: 20, background: COL.surfaceAlt, borderRadius: 10 },
  };
}
